/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useRef } from 'react';

import { throttle } from 'lodash';

import ImgixWithPrioritizedLoading from 'js/components/ImgixWithPrioritizedLoading';

import { Button, Typography2, breakpoints } from '@coursera/cds-core';
import { SearchIcon } from '@coursera/cds-icons';

import { MOBILE_HEADER_BREAKPOINT } from 'bundles/composable-page-header/constants';
import type { Maybe } from 'bundles/front-page/__generated__/baseContentfulTypes';
import type { MarketingContentFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import { isContentfulContentValid } from 'bundles/front-page/components/modules/HeroBanner';
import HeroBannerTrendingLinks from 'bundles/front-page/components/modules/hero-banner/HeroBannerTrendingLinks';
import SearchBar from 'bundles/search-common/components/searchbar/SearchBar';

import _t from 'i18n!nls/front-page';

const styles = {
  heroBanner: css`
    background-color: transparent;
    color: #1f1f1f;
  `,
  bannerContainer: css`
    align-items: center;
    display: flex;
    justify-content: center;
    margin: 0 auto;
    max-width: 1400px;
    position: relative;
    width: 100%;

    @media screen and (max-width: 767px) {
      display: block;
    }
  `,
  textContainer: css`
    margin-left: 100px;
    margin-right: 180px;
    position: relative;
    width: 570px;

    @media screen and (max-width: 1225px) {
      margin-right: 100px;
    }

    @media screen and (max-width: 1150px) {
      margin-left: 96px;
      margin-right: 32px;
      max-width: 436px;
      width: 100%;
    }

    @media screen and (max-width: 767px) {
      margin-left: auto;
      margin-right: auto;
      padding: 36px 24px 0;
      max-width: 100%;
    }

    @media screen and (max-width: 480px) {
      text-align: center;
    }
  `,
  heroSubheader: css`
    margin-top: var(--cds-spacing-300);
    margin-bottom: var(--cds-spacing-300);
    ${breakpoints.up('md')} {
      max-width: 500px;
    }
  `,
  imgContainer: css`
    height: 497px;
    width: 459px;
    margin-right: 24px;

    @media screen and (max-width: 1150px) {
      height: 354px;
      width: 328px;
    }

    @media screen and (max-width: 767px) {
      display: none;
    }
  `,
  image: css`
    width: 100%;

    @media screen and (max-width: 1150px) {
      max-height: 354px;
      max-width: 328px;
    }

    @media screen and (max-width: 767px) {
      display: none;
    }
  `,

  searchSection: css`
    ${breakpoints.down('sm')} {
      padding-top: 16px;
      max-width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
    }
  `,

  searchBarContainer: css`
    ${breakpoints.down('sm')} {
      width: 100%;
    }

    .rc-SearchBar {
      width: 100%;
      margin: 0;

      .rc-AutoComplete {
        width: 100%;
        background-color: white;

        input[type='text'] {
          border-top: 1px solid var(--cds-color-grey-200);
          border-left: 1px solid var(--cds-color-grey-200);
          border-bottom: 1px solid var(--cds-color-grey-200);
          border-right: none;
          border-radius: 100px 0 0 100px;
          height: 100%;
          padding: 0 10px 0 24px;
          box-shadow: 0 2px 4px -2px rgb(0 0 0 / 5%);
          ${breakpoints.down('sm')} {
            padding: 0 10px 0 10px;
          }
        }

        .react-autosuggest__suggestions-container--open {
          top: unset !important;
          text-align: left;

          @media (min-width: 768px) {
            width: 100%;
            border-radius: 16px;
          }
          ${breakpoints.up('md')} {
            max-width: 600px;
          }
        }
      }
    }
  `,

  desktopSearchBar: css`
    display: block;

    @media (max-width: ${MOBILE_HEADER_BREAKPOINT}px) {
      display: none;
    }
  `,
  mobileSearchBar: css`
    display: none;

    @media (max-width: ${MOBILE_HEADER_BREAKPOINT}px) {
      display: block;
    }
  `,
  // The styles for the mobile search bar here try to match the styles for the desktop search bar
  mobileSearchBarButton: css`
    border: 1px solid var(--cds-color-grey-200);
    border-radius: 100px;
    width: 100%;
    max-width: unset;
    padding: var(--cds-spacing-50) var(--cds-spacing-50) var(--cds-spacing-50) var(--cds-spacing-300);
    box-shadow: 0 2px 4px -2px rgb(0 0 0 / 5%);
    display: flex;
    justify-content: space-between;
    align-items: center;

    /* overrides hover and focus styles on the button */
    color: var(--cds-color-neutral-primary-weak) !important;
    font-weight: 300 !important;

    &:hover,
    &:focus {
      background-color: transparent;
      text-decoration: none;
      cursor: text;
    }
  `,
  mobileSearchBarButtonIcon: css`
    border-radius: 100px;
    height: 44px;
    width: 44px;
    background-color: var(--cds-color-interactive-primary);
    display: flex;
    align-items: center;
    justify-content: center;
  `,
};

type Props = {
  content?: Maybe<MarketingContentFieldsFragment>;
  setHasScrolledPastHero?: (hasScrolledPastHero: boolean) => void;
  setIsMobileSearchModalOpen?: (isMobileSearchModalOpen: boolean) => void;
};

export const HeroBanner = ({ content, setHasScrolledPastHero, setIsMobileSearchModalOpen }: Props) => {
  const imgParams = {
    quality: 40,
  };

  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = throttle(() => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setHasScrolledPastHero?.(heroBottom < 0);
      }
    }, 100);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setHasScrolledPastHero]);

  if (!isContentfulContentValid(content)) {
    return null;
  }

  const { title, description, visual, visualAlternativeText } = content;

  return (
    <section className="rc-HeroBannerRedesign" css={styles.heroBanner} data-testid="front-page-banner">
      <div css={styles.bannerContainer} ref={heroRef}>
        <div css={styles.textContainer}>
          <Typography2 variant="display" component="h1">
            {title}
          </Typography2>
          <Typography2 variant="bodyPrimary" component="p" css={styles.heroSubheader}>
            {description}
          </Typography2>
          <div css={styles.searchBarContainer}>
            <div css={styles.mobileSearchBar}>
              <Button
                onClick={() => setIsMobileSearchModalOpen?.(true)}
                css={styles.mobileSearchBarButton}
                variant="ghost"
                icon={
                  <div css={styles.mobileSearchBarButtonIcon}>
                    <SearchIcon size="medium" color="invert" />
                  </div>
                }
              >
                {_t("Search 'python' or 'front-end developer'")}
              </Button>
            </div>
            <div css={styles.desktopSearchBar}>
              <SearchBar pageLocation="FrontPageBanner" />
            </div>
            <HeroBannerTrendingLinks />
          </div>
        </div>
        {visual?.url && (
          <div css={styles.imgContainer}>
            <ImgixWithPrioritizedLoading
              css={styles.image}
              imgParams={imgParams}
              alt={visualAlternativeText || ''}
              src={visual?.url}
              maxWidth={400}
              maxHeight={432}
              style={{ marginLeft: 'auto', marginRight: 'auto', width: '100%' }}
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default HeroBanner;
